// src/components/UserContractView.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, Card, CardContent, CardActions,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Chip, Grid, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Alert, CircularProgress, Badge,
  IconButton, Tooltip, useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  CheckCircle as SignedIcon,
  Pending as PendingIcon,
  Cancel as DeclinedIcon,
  Assignment as ContractIcon,
  Description as DocumentIcon,
  OpenInNew as OpenInNewIcon,
  Link as LinkIcon,
  ContentCopy as CopyIcon,
  Close as CloseIcon,
  PictureAsPdf as PdfIcon,
  Launch as LaunchIcon
} from '@mui/icons-material';
import client from '../../API';

const BLITZ_ROOT = process.env.REACT_APP_PUBLIC_ROOT || window.location.origin;
const buildPublicLink = (assignmentId) => `${BLITZ_ROOT}/contract/${assignmentId}`;

// ------- utils -------
const statusChipProps = (status) => {
  switch (status) {
    case 'signed':
    case 'agreed':
      return { color: 'success', icon: <SignedIcon /> };
    case 'declined':
    case 'expired':
      return { color: 'error', icon: <DeclinedIcon /> };
    default:
      return { color: 'warning', icon: <PendingIcon /> };
  }
};

const dateFromSignature = (sig = {}) =>
  sig.signed_at || sig.agreed_at || sig.declined_at || sig.created_at;

const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    alert('Link copied!');
  } catch {
    alert('Failed to copy link');
  }
};

const loadContractFromAssignment = async (assignmentId) => {
  const data = await client.contracts.getContractFromAssignment(assignmentId);
  return data;
};

// ------- main component -------
const UserContractView = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [pending, setPending] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeAssignment, setActiveAssignment] = useState(null);
  const [activeSignature, setActiveSignature] = useState(null);
  const [contractData, setContractData] = useState(null);
  const [contractLoading, setContractLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [readOnly, setReadOnly] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [p, h] = await Promise.all([
        client.contracts.getMyPendingContracts(),
        client.contracts.getMySignatures(),
      ]);
      setPending(p?.pending_contracts || []);
      setHistory(h?.signatures || []);
    } catch (e) {
      console.error('Failed to fetch contract data:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const navigateToContract = (assignmentId) => {
    if (!assignmentId) {
      alert('Invalid assignment ID');
      return;
    }
    navigate(`/contract/${assignmentId}`);
  };

  // --- actions ---
  const updateSignature = (updated) => {
    const assignmentId = updated?.assignment?.id || updated?.assignment_id;
    setPending((prev) =>
      prev.filter((item) => {
        const itemAssignmentId = item?.template ? item.id : (item?.assignment?.id || item?.assignment_id);
        return itemAssignmentId !== assignmentId;
      })
    );
    setHistory((prev) => {
      const idx = prev.findIndex((sig) => (sig?.assignment?.id || sig?.assignment_id) === assignmentId);
      if (idx >= 0) {
        const clone = [...prev];
        clone[idx] = { ...clone[idx], ...updated };
        return clone;
      }
      return [...prev, updated];
    });
  };

  const handleAgreeOrSign = async (signature, isSimple) => {
    const sigId = signature?.id;
    try {
      setActionLoading(true);
      const res = isSimple
        ? await client.contracts.agreeToContract(sigId)
        : await client.contracts.signContract(sigId);
      updateSignature(res.signature || res.data?.signature || signature);
      closeDialog();
    } catch (e) {
      console.error(e);
      alert(e.message || 'Failed to complete action');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDecline = async (signature, reason) => {
    const sigId = signature?.id;
    try {
      setActionLoading(true);
      const res = await client.contracts.declineContract(sigId, reason);
      updateSignature(res.signature || res.data?.signature || signature);
      closeDialog();
    } catch (e) {
      console.error(e);
      alert(e.message || 'Failed to decline');
    } finally {
      setActionLoading(false);
    }
  };

  const openDialog = async (item, readonly = false) => {
    let assignmentId;
    let assignment;
    let signature;

    if (item?.template) {
      assignmentId = item.id;
      assignment = item;
      signature = null;
    } else {
      assignmentId = item?.assignment?.id || item?.assignment_id;
      assignment = item?.assignment;
      signature = item;
    }

    if (!assignmentId) {
      alert('Invalid assignment ID - cannot load contract');
      return;
    }

    setActiveAssignment(assignment);
    setActiveSignature(signature);
    setReadOnly(readonly);
    setDialogOpen(true);
    setContractLoading(true);
    setContractData(null);

    try {
      const fullContractData = await loadContractFromAssignment(assignmentId);
      setContractData(fullContractData);
    } catch (error) {
      console.error('Failed to load contract data:', error);
      alert('Failed to load contract details');
      closeDialog();
    } finally {
      setContractLoading(false);
    }
  };

  const closeDialog = () => {
    setActiveAssignment(null);
    setActiveSignature(null);
    setContractData(null);
    setReadOnly(false);
    setDialogOpen(false);
    setContractLoading(false);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" alignItems="center" gap={2} mb={3} flexWrap="wrap">
        <ContractIcon fontSize="large" color="primary" />
        <Box>
          <Typography variant="h4" gutterBottom>My Contracts</Typography>
          <Typography variant="body1" color="text.secondary">
            Review, sign, and download your contracts
          </Typography>
        </Box>
        {pending.length > 0 && (
          <Badge badgeContent={pending.length} color="warning" sx={{ ml: 'auto' }}>
            <PendingIcon fontSize="large" />
          </Badge>
        )}
      </Box>

      {pending.length > 0 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          You have <strong>{pending.length}</strong> pending contract{pending.length > 1 && 's'}.
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Pending */}
        <Grid item xs={12} lg={6}>
          <Typography variant="h6" gutterBottom>
            Pending ({pending.length})
          </Typography>
          <Box sx={{ maxHeight: 600, overflowY: 'auto' }}>
            {pending.length === 0 && (
              <Card>
                <CardContent>
                  <Box textAlign="center" py={4}>
                    <SignedIcon color="success" sx={{ fontSize: 64, mb: 2 }} />
                    <Typography variant="h6">All contracts are up to date!</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Nothing needs your signature.
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            )}
            {pending.map((item, idx) => {
              if (!item) return null;
              let assignmentId;
              let assignment;
              let template;
              let isSimple;

              if (item?.template) {
                assignmentId = item.id;
                assignment = item;
                template = item.template;
                isSimple = item.is_simple;
              } else {
                assignmentId = item?.assignment?.id || item?.assignment_id;
                assignment = item?.assignment;
                template = assignment?.template || {};
                isSimple = assignment?.is_simple;
              }

              const publicLink = assignmentId ? buildPublicLink(assignmentId) : null;

              return (
                <Card key={assignmentId || idx} sx={{ mb: 2 }}>
                  <CardContent>
                    <Box display="flex" gap={2} alignItems="flex-start">
                      <DocumentIcon color="primary" />
                      <Box flex={1}>
                        <Box display="flex" gap={1} alignItems="center" mb={0.5}>
                          <Typography variant="h6">
                            {template?.name || 'Contract'}
                          </Typography>
                          <Chip
                            size="small"
                            label={isSimple ? 'Terms' : 'Contract'}
                            color={isSimple ? 'info' : 'primary'}
                            variant="outlined"
                          />
                        </Box>
                        {template?.description && (
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            {template.description}
                          </Typography>
                        )}
                        {assignment?.assignment_message && (
                          <Alert severity="info" sx={{ mt: 1 }}>
                            <Typography variant="body2">
                              <strong>Message:</strong> {assignment.assignment_message}
                            </Typography>
                          </Alert>
                        )}
                        {assignment?.due_date && (
                          <Typography variant="body2" color="warning.main" sx={{ mt: 0.5 }}>
                            <strong>Due:</strong> {new Date(assignment.due_date).toLocaleDateString()}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </CardContent>
                  <CardActions sx={{ pt: 0, justifyContent: 'space-between', flexWrap: 'wrap' }}>
                    <Box display="flex" gap={1} flexWrap="wrap">
                      <Button
                        variant="contained"
                        startIcon={<LaunchIcon />}
                        onClick={() => navigateToContract(assignmentId)}
                        disabled={!assignmentId}
                      >
                        Open Contract
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<DocumentIcon />}
                        onClick={() => openDialog(item, false)}
                        disabled={!assignmentId}
                      >
                        Quick Preview
                      </Button>
                    </Box>
                    {publicLink && assignmentId && (
                      <Box display="flex" gap={1} alignItems="center">
                        <Tooltip title="Open link (no login)">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => window.open(publicLink, '_blank', 'noopener')}
                          >
                            <OpenInNewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Copy link">
                          <IconButton
                            size="small"
                            onClick={() => copyToClipboard(publicLink)}
                          >
                            <CopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}
                  </CardActions>
                </Card>
              );
            })}
          </Box>
        </Grid>

        {/* History */}
        <Grid item xs={12} lg={6}>
          <Typography variant="h6" gutterBottom>Signature History</Typography>

          {/* MOBILE: cards */}
          {isMobile ? (
            <Box sx={{ maxHeight: 600, overflowY: 'auto' }}>
              {history.length === 0 && (
                <Card sx={{ mb: 2 }}>
                  <CardContent>
                    <Typography variant="body2" color="text.secondary" align="center">
                      No signatures yet.
                    </Typography>
                  </CardContent>
                </Card>
              )}
              {history.map((signature, idx) => {
                if (!signature) return null;
                const assignmentId = signature?.assignment?.id || signature?.assignment_id;
                const assignment = signature?.assignment || {};
                const template = assignment?.template || {};
                const { color, icon } = statusChipProps(signature.status);
                const publicLink = assignmentId ? buildPublicLink(assignmentId) : null;

                return (
                  <Card key={assignmentId || idx} sx={{ mb: 2 }}>
                    <CardContent>
                      <Box display="flex" gap={2}>
                        <DocumentIcon color="primary" />
                        <Box flex={1}>
                          <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
                            <Typography variant="h6">
                              {template?.name || 'Contract'}
                            </Typography>
                            <Chip icon={icon} label={signature.status} color={color} size="small" />
                          </Box>
                          <Typography variant="body2" sx={{ mt: .5 }}>
                            {new Date(dateFromSignature(signature)).toLocaleDateString()}
                          </Typography>
                          {signature.decline_reason && (
                            <Typography variant="caption" color="error.main" display="block" sx={{ mt: .5 }}>
                              Reason: {signature.decline_reason}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </CardContent>
                    <CardActions sx={{ pt: 0, justifyContent: 'space-between', flexWrap: 'wrap' }}>
                      <Box display="flex" gap={1} flexWrap="wrap">
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<LaunchIcon />}
                          onClick={() => navigateToContract(assignmentId)}
                          disabled={!assignmentId}
                        >
                          Open
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<DocumentIcon />}
                          onClick={() => openDialog(signature, true)}
                          disabled={!assignmentId}
                        >
                          Preview
                        </Button>
                      </Box>
                      {publicLink && (
                        <Box display="flex" gap={1}>
                          <Tooltip title="Open public link">
                            <IconButton
                              size="small"
                              onClick={() => window.open(publicLink, '_blank', 'noopener')}
                            >
                              <OpenInNewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Copy link">
                            <IconButton
                              size="small"
                              onClick={() => copyToClipboard(publicLink)}
                            >
                              <CopyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                    </CardActions>
                  </Card>
                );
              })}
            </Box>
          ) : (
            // DESKTOP: table
            <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Contract</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {history.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 6 }}>
                        <Typography variant="body2" color="text.secondary">
                          No signatures yet.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {history.map((signature, idx) => {
                    if (!signature) return null;
                    const assignmentId = signature?.assignment?.id || signature?.assignment_id;
                    const assignment = signature?.assignment || {};
                    const template = assignment?.template || {};
                    const { color, icon } = statusChipProps(signature.status);
                    const publicLink = assignmentId ? buildPublicLink(assignmentId) : null;

                    return (
                      <TableRow key={assignmentId || idx} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {template?.name || 'Contract'}
                          </Typography>
                          {signature.decline_reason && (
                            <Typography variant="caption" color="error.main">
                              Reason: {signature.decline_reason}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip icon={icon} label={signature.status} color={color} size="small" />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {new Date(dateFromSignature(signature)).toLocaleDateString()}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Open contract">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => navigateToContract(assignmentId)}
                              disabled={!assignmentId}
                            >
                              <LaunchIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Quick preview">
                            <IconButton
                              size="small"
                              onClick={() => openDialog(signature, true)}
                              disabled={!assignmentId}
                            >
                              <DocumentIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {publicLink && (
                            <>
                              <Tooltip title="Open public link">
                                <IconButton
                                  size="small"
                                  onClick={() => window.open(publicLink, '_blank', 'noopener')}
                                >
                                  <OpenInNewIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Copy link">
                                <IconButton
                                  size="small"
                                  onClick={() => copyToClipboard(publicLink)}
                                >
                                  <CopyIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Grid>
      </Grid>

      <EnhancedSignDialog
        open={dialogOpen}
        assignment={activeAssignment}
        signature={activeSignature}
        contractData={contractData}
        loading={contractLoading}
        onClose={closeDialog}
        onAgree={handleAgreeOrSign}
        onDecline={handleDecline}
        actionLoading={actionLoading}
        readOnly={readOnly || activeSignature?.status !== 'pending'}
        onNavigateToContract={() => {
          const assignmentId = activeAssignment?.id || activeSignature?.assignment?.id || activeSignature?.assignment_id;
          if (assignmentId) {
            closeDialog();
            navigateToContract(assignmentId);
          }
        }}
      />
    </Box>
  );
};

// -------- Enhanced Dialog --------
const EnhancedSignDialog = ({ 
  open, 
  assignment,
  signature, 
  contractData, 
  loading: contractLoading, 
  onClose, 
  onAgree, 
  onDecline, 
  actionLoading, 
  readOnly,
  onNavigateToContract 
}) => {
  const [declineMode, setDeclineMode] = useState(false);
  const [reason, setReason] = useState('');
  const contentRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setDeclineMode(false);
      setReason('');
    }
  }, [open]);

  if (!assignment) return null;

  const template = assignment?.template || {};
  const isSimple = assignment?.is_simple;
  const assignmentId = assignment?.id;
  const publicLink = assignmentId ? buildPublicLink(assignmentId) : null;

  const processedBody = contractData?.processed_contract_body || 
                       contractData?.template?.contract_body || 
                       assignment.processed_contract_body || 
                       template.contract_body || 
                       '';

  const downloadPdf = async () => {
    if (!contentRef.current) return;
    const [{ default: html2canvas }, { default: jsPDF }] = await Promise.all([
      import('html2canvas'),
      import('jspdf')
    ]);

    const canvas = await html2canvas(contentRef.current, { scale: 2 });
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const imgWidth = pageWidth;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    let position = 0;
    let heightLeft = imgHeight;

    pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;
    while (heightLeft > 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }
    pdf.save(`${template?.name || 'contract'}.pdf`);
  };

  const showActionButtons = !readOnly && signature?.status === 'pending';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        <Box display="flex" alignItems="center" gap={2}>
          <DocumentIcon color="primary" />
          {template?.name || 'Contract'}
          <Chip size="small" label="Preview" color="info" variant="outlined" />
          {publicLink && (
            <Tooltip title="Open public link">
              <IconButton
                size="small"
                sx={{ ml: 'auto' }}
                onClick={() => window.open(publicLink, '_blank', 'noopener')}
              >
                <LinkIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ backgroundColor: '#fafafa' }}>
        {assignment?.assignment_message && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2"><strong>Message:</strong> {assignment.assignment_message}</Typography>
          </Alert>
        )}
        {assignment?.due_date && signature?.status === 'pending' && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Due Date:</strong> {new Date(assignment.due_date).toLocaleString()}
            </Typography>
          </Alert>
        )}

        {contractLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <CircularProgress />
            <Typography variant="body2" sx={{ ml: 2 }}>Loading contract...</Typography>
          </Box>
        ) : (
          <Box
            ref={contentRef}
            sx={{
              position: 'relative',
              maxHeight: 400,
              overflow: 'auto',
              border: '1px solid #e0e0e0',
              borderRadius: 1,
              p: 2,
              mb: 3,
              backgroundColor: '#fff',
              fontFamily: 'Arial, sans-serif',
              lineHeight: 1.6
            }}
          >
            {processedBody ? (
              <div dangerouslySetInnerHTML={{ __html: processedBody }} />
            ) : (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <Typography variant="body2" color="text.secondary">
                  Contract content not available
                </Typography>
              </Box>
            )}

            {signature?.status && signature.status !== 'pending' && (
              <Box
                sx={{
                  position: 'absolute',
                  right: 16,
                  bottom: 16,
                  textAlign: 'right',
                  border: '1px solid #ccc',
                  p: 1.5,
                  borderRadius: 1,
                  background: '#fafafa',
                  maxWidth: 200
                }}
              >
                <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>
                  {signature.status === 'signed'
                    ? 'Digitally Signed'
                    : signature.status === 'agreed'
                    ? 'Agreed'
                    : 'Declined'}
                </Typography>
                <Typography variant="caption" sx={{ display: 'block' }}>
                  {new Date(dateFromSignature(signature)).toLocaleString()}
                </Typography>
                {signature?.ip_address && (
                  <Typography variant="caption" sx={{ display: 'block' }}>
                    IP: {signature.ip_address}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        )}

        {!readOnly && signature?.status === 'pending' && declineMode && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" gutterBottom>
              Please provide a reason for declining:
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Enter your reason..."
              sx={{ mb: 2 }}
            />
          </Box>
        )}

        <Alert severity="info">
          <Typography variant="body2">
            {isSimple
              ? 'By clicking "I Agree", you confirm that you have read and agree to be bound by the terms above.'
              : 'By clicking "Sign Contract", you agree to be legally bound by the terms and conditions above. Your digital acceptance will be recorded.'}
          </Typography>
        </Alert>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button 
          onClick={onNavigateToContract} 
          startIcon={<LaunchIcon />} 
          disabled={actionLoading}
          variant="outlined"
        >
          Open Full Page
        </Button>
        <Button onClick={downloadPdf} startIcon={<PdfIcon />} disabled={actionLoading}>
          Download PDF
        </Button>
        <Button onClick={onClose} disabled={actionLoading}>Close</Button>

        {(!readOnly && signature?.status === 'pending') && (
          !declineMode ? (
            <>
              <Button
                variant="outlined"
                color="error"
                onClick={() => setDeclineMode(true)}
                disabled={actionLoading}
              >
                Decline
              </Button>
              <Button
                variant="contained"
                onClick={() => onAgree(signature, isSimple)}
                disabled={actionLoading}
                startIcon={actionLoading ? <CircularProgress size={16} /> : <DocumentIcon />}
              >
                {actionLoading ? 'Processing...' : isSimple ? 'I Agree' : 'Sign Contract'}
              </Button>
            </>
          ) : (
            <Button
              variant="contained"
              color="error"
              onClick={() => onDecline(signature, reason)}
              disabled={!reason.trim() || actionLoading}
              startIcon={actionLoading ? <CircularProgress size={16} /> : <DeclinedIcon />}
            >
              {actionLoading ? 'Submitting...' : 'Confirm Decline'}
            </Button>
          )
        )}
      </DialogActions>
    </Dialog>
  );
};

// --- small widget ---
export const ContractWidget = () => {
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { pending_contracts = [] } = await client.contracts.getMyPendingContracts();
      setCount(pending_contracts.length);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <Card sx={{ p: 2 }}>
        <CircularProgress size={20} />
      </Card>
    );
  }

  if (count === 0) return null;

  return (
    <Card sx={{ p: 2, bgcolor: '#fff3cd', borderColor: '#ffeaa7', border: '1px solid' }}>
      <Box display="flex" alignItems="center" gap={2}>
        <Badge badgeContent={count} color="warning">
          <ContractIcon color="warning" />
        </Badge>
        <Box flex={1}>
          <Typography variant="h6" color="warning.dark">Pending Contracts</Typography>
          <Typography variant="body2">You have {count} contract{count > 1 && 's'} waiting</Typography>
        </Box>
        <Button variant="outlined" size="small" href="/contracts">
          Review
        </Button>
      </Box>
    </Card>
  );
};

export default UserContractView;
